import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import QRCode from 'qrcode'
import { supabase } from '../../lib/supabase'
import { AdminService } from '../../services/adminService'
import { SessionManager } from '../../services/sessionManager'

const MFASetup = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [factorId, setFactorId] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [error, setError] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const maxRetries = 5

  // 🔥 Check if this is an MFA reset flow
  const isMFAReset = location.state?.mfaReset === true

  const handleEnrollmentSuccess = React.useCallback(async (data) => {
    console.log('✅ MFA enrollment successful')

    const totpUri = data.totp.uri
    const totpSecret = data.totp.secret
    const factorIdValue = data.id

    setSecret(totpSecret)
    setFactorId(factorIdValue)

    // Generate QR code
    const qrCodeDataUrl = await QRCode.toDataURL(totpUri, {
      width: 256,
      margin: 2,
      errorCorrectionLevel: 'M'
    })

    setQrCode(qrCodeDataUrl)
    setLoading(false)

    console.log('✅ QR code generated, factorId:', factorIdValue)
  }, [])

  const enrollMFA = React.useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      console.log('📱 Starting MFA enrollment...')

      // Check existing factors
      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors()

      if (listError) {
        console.error('❌ Failed to list MFA factors:', listError)
      } else {
        console.log('   Existing MFA factors:', factors)
      }

      // Get all factors
      const allFactors = factors?.all || []
      const totpFactors = factors?.totp || []

      console.log('   allFactors:', allFactors.length, 'totpFactors:', totpFactors.length)

      // 🔥 GOLDEN BADGE FIX: Check if there's already a VERIFIED factor
      // This auto-repairs users who finished setup but DB sync failed
      // ⚠️ SKIP THIS CHECK IF MFA RESET IS REQUESTED
      if (!isMFAReset) {
        const { repaired, hasVerifiedMFA } = await AdminService.syncMFAStatus()

        if (hasVerifiedMFA || repaired) {
          console.log('✅ User already has verified MFA (or repaired), redirecting to dashboard...')

          // 🔥 FIX: Bond trust even when skipping setup
          let currentSessionId = SessionManager.getCurrentSession()
          if (!currentSessionId) {
            currentSessionId = SessionManager.generateSessionId()
            SessionManager.setCurrentSession(currentSessionId)
          }

          sessionStorage.setItem('admin_mfa_verified_session_id', currentSessionId)
          localStorage.setItem('admin_mfa_verified_session_id', currentSessionId)
          console.log('🔗 MFA trust bonded to session (from skip setup):', currentSessionId)

          const targetPath = location.state?.from || '/admin'
          navigate(targetPath, { replace: true })
          return
        }
      }

      // Determine which factors to unenroll
      // If Reset mode: Use RPC to wipe ALL factors (bypassing AAL2)
      // If Normal setup: Unenroll only UNVERIFIED factors manually
      if (isMFAReset) {
        console.log('🔄 MFA Reset mode: Assuming clean state via DB trigger...')
        // No RPC call needed. AdminLogin.js updated the profile, which fired the trigger.
        setLoading(false)
      } else {
        // Normal setup: Unenroll unverified factors manually
        const unverifiedFactors = [...allFactors, ...totpFactors].filter(f => !f.verified && f.status !== 'verified')
        for (const factor of unverifiedFactors) {
          console.log('   Unenrolling unverified factor:', factor.id)
          try {
            await supabase.auth.mfa.unenroll({ factorId: factor.id })
            console.log('   ✅ Unenrolled')
          } catch (e) {
            console.log('   ⚠️ Could not unenroll (may require AAL2):', e.message)
          }
          // Wait a bit
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }

      // Enroll new TOTP factor
      // 🔥 GOLDEN PATH: If this is MFA reset, use a unique name to avoid conflicts with old factors
      const friendlyName = isMFAReset
        ? `Wonder Beauties Admin ${Date.now()}`
        : 'Wonder Beauties Admin'

      console.log('   Enrolling new TOTP factor...')
      console.log('   Friendly name:', friendlyName)

      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: friendlyName
      })

      if (enrollError) {
        console.error('❌ MFA enrollment error:', enrollError)

        // If name conflict, retry with unique name
        if (enrollError.message?.includes('already exists')) {
          console.log('   Retrying with unique name...')
          const retryResult = await supabase.auth.mfa.enroll({
            factorType: 'totp',
            friendlyName: `Wonder Beauties Admin ${Date.now()}`
          })

          if (retryResult.error) {
            setError(`Failed to set up MFA: ${retryResult.error.message}`)
            setLoading(false)
            return
          }

          // Use the successful retry data
          handleEnrollmentSuccess(retryResult.data)
          return
        }

        setError(`Failed to set up MFA: ${enrollError.message}`)
        setLoading(false)
        return
      }

      handleEnrollmentSuccess(data)

    } catch (error) {
      console.error('❌ MFA setup error:', error)
      setError(`Setup failed: ${error.message}`)
      setLoading(false)
    }
  }, [navigate, location, handleEnrollmentSuccess, isMFAReset])

  useEffect(() => {
    enrollMFA()
  }, [enrollMFA])

  const copySecret = () => {
    navigator.clipboard.writeText(secret)
    alert('Secret copied to clipboard!')
  }

  const handleVerify = React.useCallback(async (e) => {
    e.preventDefault()

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a 6-digit code')
      return
    }

    if (!factorId) {
      setError('System error: No factor ID. Please refresh the page.')
      return
    }

    if (retryCount >= maxRetries) {
      setError('Maximum retry attempts reached. Please refresh the page and try again.')
      return
    }

    try {
      setVerifying(true)
      setError('')

      console.log('🔐 Verifying TOTP code...')
      console.log('   Factor ID:', factorId)

      // Create challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: factorId
      })

      if (challengeError) {
        console.error('❌ Challenge error:', challengeError)
        setError(`Verification failed: ${challengeError.message}`)
        setVerifying(false)
        setRetryCount(retryCount + 1)
        return
      }

      console.log('✅ Challenge created:', challengeData.id)

      // Verify code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challengeData.id,
        code: verificationCode
      })

      if (verifyError) {
        console.error('❌ Verify error:', verifyError)

        // Suggest time sync if it fails
        let errorMessage = `Invalid code. ${maxRetries - retryCount - 1} attempts remaining.`
        if (maxRetries - retryCount - 1 <= 2) {
          errorMessage += ' Check if your phone time is set to "Automatic".'
        }

        setError(errorMessage)
        setVerifying(false)
        setRetryCount(retryCount + 1)
        setVerificationCode('')
        return
      }

      console.log('✅ MFA verified successfully!')

      // Mark MFA as enabled in admin_users table
      const result = await AdminService.markMFAEnabled()

      if (!result.success) {
        console.error('❌ Failed to mark MFA enabled:', result.error)
        setError(`MFA setup incomplete: ${result.error}`)
        setVerifying(false)
        return
      }

      console.log('✅ MFA enabled in database')

      // 🔥 FIX: Bond trust to the current session/tab so SecureAdminRoute doesn't boot us
      let currentSessionId = SessionManager.getCurrentSession()
      if (!currentSessionId) {
        currentSessionId = SessionManager.generateSessionId()
        SessionManager.setCurrentSession(currentSessionId)
      }

      sessionStorage.setItem('admin_mfa_verified_session_id', currentSessionId)
      localStorage.setItem('admin_mfa_verified_session_id', currentSessionId) // Fallback sync
      console.log('🔗 MFA trust bonded after setup:', currentSessionId)

      // 🔥 Deep-link restoration: Redirect back to where we came from OR dashboard
      const targetPath = location.state?.from || '/admin'
      console.log('🚀 Redirecting to:', targetPath)

      // Redirect to dashboard
      setTimeout(() => {
        navigate(targetPath, { replace: true })
      }, 1000)

    } catch (error) {
      console.error('❌ Verification error:', error)
      setError(`Verification failed: ${error.message}`)
      setVerifying(false)
      setRetryCount(retryCount + 1)
    }
  }, [verificationCode, factorId, retryCount, navigate, location])

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setVerificationCode(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="ltr">
        <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Setting up Two-Factor Authentication...</h2>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A73A5D] mx-auto"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="ltr">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-xl p-8">
        {/* 🔥 MFA Reset Success Banner */}
        {isMFAReset && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">MFA Reset Successful</h3>
                <p className="text-sm text-green-700 mt-1">
                  Your previous authenticator has been removed. Please set up a new one to continue.
                </p>
              </div>
            </div>
          </div>
        )}

        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
          {isMFAReset ? '🔐 Set Up New Authenticator' : '🔐 Set Up Two-Factor Authentication'}
        </h2>
        <p className="text-center text-gray-600 text-sm mb-6">
          {isMFAReset
            ? 'Scan the QR code below with your authenticator app to set up your new device.'
            : 'Scan the QR code below with your authenticator app to complete setup.'}
        </p>

        {/* QR Code Display */}
        <div className="text-center mb-6">
          {qrCode && (
            <img
              src={qrCode}
              alt="MFA QR Code"
              className="w-64 h-64 mx-auto border-2 border-gray-300 rounded-lg p-2 bg-white"
            />
          )}
        </div>

        {/* Manual Secret Entry */}
        <div className="bg-gray-100 p-4 rounded-lg mb-6">
          <p className="text-xs text-gray-600 mb-2">
            Can't scan? Enter this secret manually:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white p-2 rounded text-sm font-mono break-all">
              {secret}
            </code>
            <button
              type="button"
              onClick={copySecret}
              className="px-4 py-2 bg-[#A73A5D] hover:bg-[#8F2F4D] text-white rounded transition duration-200 text-sm"
            >
              Copy
            </button>
          </div>
        </div>

        {/* Recommended Apps */}
        <div className="bg-blue-50 p-4 rounded-lg mb-6 text-sm">
          <p className="font-semibold mb-2">
            📱 Recommended Authenticator Apps:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Google Authenticator (iOS/Android)</li>
            <li>Microsoft Authenticator (iOS/Android)</li>
            <li>Authy (iOS/Android/Desktop)</li>
          </ul>
        </div>

        {/* Verification Form */}
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter 6-digit code from your app:
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={handleCodeChange}
              placeholder="000000"
              disabled={verifying}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent text-center text-2xl tracking-widest font-mono"
              autoFocus
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={verifying || verificationCode.length !== 6 || retryCount >= maxRetries}
            className="w-full bg-[#A73A5D] hover:bg-[#8F2F4D] disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
          >
            {verifying ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Verifying...
              </>
            ) : (
              'Verify & Complete Setup'
            )}
          </button>

          <div className="text-center text-xs text-gray-600 space-y-4">
            {retryCount > 0 && (
              <div className="space-y-2">
                <p className="text-red-500 font-medium font-semibold">
                  {retryCount >= maxRetries ? 'Maximum attempts reached.' : `Attempts remaining: ${maxRetries - retryCount}`}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setRetryCount(0)
                    setError('')
                    enrollMFA()
                  }}
                  className="text-[#A73A5D] hover:underline font-medium block mx-auto py-2"
                >
                  🔄 Reset & Try New QR Code
                </button>
              </div>
            )}

            <div className="bg-orange-50 p-3 rounded border border-orange-100 text-orange-800">
              <p className="font-semibold mb-1">⏱️ Code Not Working?</p>
              <p>Ensure your phone's date and time are set to <strong>"Set Automatically"</strong> in settings. TOTP codes are strictly time-dependent.</p>
            </div>

            <p className="pt-2">
              Having trouble? Contact your administrator for help.
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MFASetup
